import { type } from 'arktype';
import type { TranslatableText } from '@zag/core/common';
import { translatableTextSchema } from '@zag/core/models';

export const SKILL_TRANSLATABLE_FIELDS = ['name', 'description'] as const;
export type SkillTranslatableField = (typeof SKILL_TRANSLATABLE_FIELDS)[number];
export type SkillTranslations = TranslatableText<SkillTranslatableField>;

export const skillQuerySchema = type({
  'name?': 'string',
});

export type SkillQuery = typeof skillQuerySchema.t;

export interface SkillData {
  customProperties?: Record<string, unknown>;
}

export const ratingDisplayTypeSchema = type("'stars' | 'slider' | 'percentage'");
export type RatingDisplayType = typeof ratingDisplayTypeSchema.t;

export const skillRatingConfigSchema = type({
  type: ratingDisplayTypeSchema.configure({
    description: "Rating UI control type. 'stars' shows star icons, 'slider' a draggable slider, 'percentage' a numeric 0–100 input.",
    examples: ['stars'],
  }),
  'scale?': type('number').configure({
    description: 'Upper bound of the rating scale (e.g. 5 for a 5-star control).',
    examples: [5],
  }),
}).configure({ description: 'Display configuration for employee skill ratings.' });
export type SkillRatingConfig = typeof skillRatingConfigSchema.t;

export const createSkillSchema = type({
  'name?': type('string').configure({
    description: 'Display name of the skill. Optional if `translations.en.name` is supplied.',
    examples: ['First Aid', 'Forklift Licence'],
  }),
  'description?': type('string | null').configure({
    description: 'Free-form description. `null` clears the value.',
    examples: ['Certified first responder.'],
  }),
  'icon?': type('string | null').configure({
    description: 'Material Icons identifier. `null` clears the icon.',
    examples: ['medical_services'],
  }),
  'color?': type('string | null').configure({
    description: 'CSS accent colour, typically a six-digit hex. `null` clears the colour.',
    examples: ['#0055aa'],
  }),
  'imageUrl?': type('string | null').configure({
    description: 'URL to an illustrative image. `null` clears the image.',
  }),
  'data?': type('object | null').configure({
    description:
      'Custom-property bag keyed by the definition `key`. Values are validated against the tenant custom-property definitions for `skill` entities. `null` clears the bag.',
    examples: [{ certificationLevel: 'advanced', renewalDate: '2026-12-31' }],
  }),
  'rating?': skillRatingConfigSchema.or(type('null')).configure({
    description: 'Rating-control configuration for this skill. `null` disables ratings for this skill.',
  }),
  'translations?': translatableTextSchema(SKILL_TRANSLATABLE_FIELDS).configure({
    description:
      'Per-language translations for `name` and `description`. When provided, ' +
      '`translations.en.<field>` is mirrored into the legacy column for back-compat.',
  }),
}).configure({
  description: 'Payload for creating a new skill.',
  examples: [
    {
      name: 'Erste Hilfe BLS-AED',
      data: { certificationLevel: 'advanced' },
      translations: { en: { name: 'First Aid BLS-AED' }, de: { name: 'Erste Hilfe BLS-AED' } },
    },
  ],
});

export type CreateSkill = typeof createSkillSchema.t;

export const updateSkillSchema = type({
  'name?': type('string').configure({
    description: 'Updated display name.',
    examples: ['First Aid'],
  }),
  'description?': type('string | null').configure({
    description: 'Updated description. `null` clears the value.',
  }),
  'icon?': type('string | null').configure({
    description: 'Updated Material Icons identifier. `null` clears the icon.',
    examples: ['medical_services'],
  }),
  'color?': type('string | null').configure({
    description: 'Updated accent colour. `null` clears the colour.',
    examples: ['#0055aa'],
  }),
  'imageUrl?': type('string | null').configure({
    description: 'Updated image URL. `null` clears the image.',
  }),
  'data?': type('object | null').configure({
    description: 'Updated custom-property bag keyed by the definition `key`. `null` clears the bag.',
    examples: [{ certificationLevel: 'expert' }],
  }),
  'rating?': skillRatingConfigSchema.or(type('null')).configure({
    description: 'Updated rating-control configuration. `null` disables ratings.',
  }),
  'translations?': translatableTextSchema(SKILL_TRANSLATABLE_FIELDS).configure({
    description:
      'Per-language translations to merge into the existing translations map. ' +
      'Languages and fields omitted are preserved. Setting a field to `null` clears it.',
  }),
}).configure({
  description: 'Partial update of a skill — fields omitted from the body remain unchanged.',
  examples: [{ data: { certificationLevel: 'expert' }, translations: { de: { name: 'Erste Hilfe BLS-AED' } } }],
});

export type UpdateSkill = typeof updateSkillSchema.t;

/**
 * Response type for Skill entity from API.
 * Includes computed fields like employeeCount.
 */
export interface SkillResponse {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  imageUrl: string | null;
  data: SkillData | null;
  rating: SkillRatingConfig | null;
  employeeCount?: number;
  /** Full per-language translations map. Only populated when the caller asked for it via `?include=translations`. */
  translations?: TranslatableText<string>;
  createdAt: string;
  updatedAt: string;
}
